import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgForage } from 'ngforage';
import * as allSettled from 'promise.allsettled';
import AssetHelper from 'src/app/helpers/asset-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import Asset from 'src/app/model/asset';
import { ImageCompressionService } from '../../image-compression.service';
import { LoadingService } from '../../loading.service';
import { SnackService } from '../../snack.service';
import { Observable } from 'rxjs';
import { _post } from '../../base.service';

@Injectable({
  providedIn: 'root',
})
export class TourInstallerSubmitImmoTaskAssetControllerRequestService {
  private readonly compressIfLargerThan = 524288; // 0.5 MB
  private static BASE_URL = '/tourInstaller';
  private readonly post: <T>(
    path: string,
    body: RequestBody,
    deserialize: (response: any) => T
  ) => Observable<T>;

  private readonly deserialize = (res) => res as ImmoTaskAssetSubmitResult;

  constructor(
    http: HttpClient,
    private snackService: SnackService,
    loadingService: LoadingService,
    router: Router,
    private ngForage: NgForage,
    private imageCompressionService: ImageCompressionService
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.post = _post(
      TourInstallerSubmitImmoTaskAssetControllerRequestService.BASE_URL,
      requestServices
    );
  }

  public async submitAsset(
    asset: Asset,
    immoTaskId: number,
    immediateSubmit: boolean,
    showErrorMessage = false
  ): Promise<ImmoTaskAssetSubmitResult> {
    const file = await this.ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(asset.localFileUuid)
    );
    if (file == null) {
      throw new Error(`File no longer exists: ${asset.localFileUuid}`);
    }
    const compressedFile =
      file.size > this.compressIfLargerThan
        ? await this.imageCompressionService.compressImage(file)
        : file;
    const base64 = await AssetHelper.extractBase64(compressedFile);
    const requestBody: RequestBody = {
      name: compressedFile.name,
      type: compressedFile.type,
      size: compressedFile.size,
      base64: base64,
      localFileUuid: asset.localFileUuid,
      immoTaskId: immoTaskId,
    };
    try {
      return await this.post(
        '/submitImmoTaskAsset',
        requestBody,
        this.deserialize
      ).toPromise();
    } catch (err) {
      if (
        ErrorHelper.isConnectionLostError(err) &&
        immediateSubmit &&
        showErrorMessage
      ) {
        this.snackService.showWarning(
          'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
        );
      } else if (immediateSubmit && showErrorMessage) {
        this.snackService.showError(err);
      }
      throw err;
    }
  }

  public async submitMultipleAssets(
    assets: Asset[],
    immoTaskId: number,
    immediateSubmit: boolean
  ): Promise<MultipleImmOTaskAssetSubmitResult> {
    const results = await allSettled(
      assets.map((a) => this.submitAsset(a, immoTaskId, immediateSubmit))
    );
    const resultMap = new Map<string, ImmoTaskAssetSubmitResult>();
    let error = null;
    for (const result of results) {
      if (result.status === 'fulfilled') {
        resultMap.set(result.value.localFileUuid, result.value);
      } else if (result.status === 'rejected') {
        error =
          error == null
            ? result.reason
            : ErrorHelper.isConnectionLostError(error)
            ? error
            : result.reason;
      }
    }
    return {
      resultMap: resultMap,
      error: error,
    };
  }
}

interface RequestBody {
  name: string;
  type: string;
  size: number;
  base64: string;
  localFileUuid: string;
  immoTaskId: number;
}

export interface ImmoTaskAssetSubmitResult {
  id: number;
  asset: { uuid: string };
  localFileUuid: string;
}

export interface MultipleImmOTaskAssetSubmitResult {
  resultMap: Map<string, ImmoTaskAssetSubmitResult>;
  error?: any;
}
